import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
import { StepList } from '../components/StepList';
export function MobileIntegrationPage() {
  const iosSteps = [{
    title: 'Install the SDK',
    content: <CodeBlock language="Ruby" title="Podfile" code={`# Add to your Podfile
pod 'GatewaySDK', '~> 1.0'

# Then run
pod install`} data-id="element-1745" />
  }, {
    title: 'Configure the gateway',
    content: <CodeBlock language="Swift" title="AppDelegate.swift" code={`import GatewaySDK

func application(_ application: UIApplication,
    didFinishLaunchingWithOptions launchOptions: [UIApplication.LaunchOptionsKey: Any]?) -> Bool {
    
    Gateway.shared.configure(
        region: .testGateway,
        merchantId: "YOUR_MERCHANT_ID",
        apiVersion: 72
    )
    
    return true
}`} data-id="element-1746" />
  }, {
    title: 'Collect card details and pay',
    content: <CodeBlock language="Swift" title="PaymentViewController.swift" code={`// Update the session with card details
Gateway.shared.updateSession("SESSION_ID",
    nameOnCard: nameField.text ?? "",
    cardNumber: cardNumberField.text ?? "",
    securityCode: cvvField.text ?? "",
    expiryMM: expiryMonthField.text ?? "",
    expiryYY: expiryYearField.text ?? ""
) { result in
    switch result {
    case .success(let response):
        print("Session updated: \\(response)")
        // Complete payment on your server
    case .failure(let error):
        print("Error: \\(error)")
    }
}`} data-id="element-1747" />
  }];
  const androidSteps = [{
    title: 'Add the dependency',
    content: <CodeBlock language="Groovy" title="build.gradle" code={`dependencies {
    implementation 'com.mastercard.gateway:gateway-android-sdk:1.0.0'
}`} data-id="element-1748" />
  }, {
    title: 'Initialize the SDK',
    content: <CodeBlock language="Kotlin" title="Application.kt" code={`import com.mastercard.gateway.android.sdk.Gateway

class MyApplication : Application() {
    override fun onCreate() {
        super.onCreate()
        
        Gateway.initialize(
            region = Gateway.Region.TEST,
            merchantId = "YOUR_MERCHANT_ID",
            apiVersion = 72
        )
    }
}`} data-id="element-1749" />
  }, {
    title: 'Process the payment',
    content: <CodeBlock language="Kotlin" title="PaymentActivity.kt" code={`Gateway.updateSession(
    sessionId = "SESSION_ID",
    cardNumber = cardNumberInput.text.toString(),
    securityCode = cvvInput.text.toString(),
    expiryMM = expiryMonth.text.toString(),
    expiryYY = expiryYear.text.toString(),
    callback = object : GatewayCallback {
        override fun onSuccess(response: GatewayResponse) {
            // Session updated, complete payment on server
            Log.d("Payment", "Session: \${response.sessionId}")
        }
        override fun onError(error: GatewayError) {
            Log.e("Payment", "Error: \${error.message}")
        }
    }
)`} data-id="element-1750" />
  }];
  return <DocPageLayout title="Mobile Integration" description="Integrate payment processing into your iOS and Android mobile applications using our native SDKs." data-id="element-1751">
      <p className="text-gray-700 mb-6" data-id="element-1752">
        Our Mobile SDKs provide native libraries for iOS and Android that allow
        you to securely collect payment details within your mobile app. The SDKs
        handle sensitive card data securely and communicate directly with the
        gateway, reducing your PCI compliance scope.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1753">
        Platform Support
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1754">
        <div className="p-6 border border-gray-200 rounded-lg" data-id="element-1755">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1756">iOS</h3>
          <p className="text-sm text-gray-600" data-id="element-1757">
            iOS 12.0+ · Swift 5.0+ · Xcode 14+
          </p>
        </div>
        <div className="p-6 border border-gray-200 rounded-lg" data-id="element-1758">
          <h3 className="font-bold text-gray-900 mb-2" data-id="element-1759">Android</h3>
          <p className="text-sm text-gray-600" data-id="element-1760">
            API Level 21+ · Kotlin 1.6+ · Android Studio
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1761">
        iOS Integration
      </h2>
      <StepList steps={iosSteps} data-id="element-1762" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1763">
        Android Integration
      </h2>
      <StepList steps={androidSteps} data-id="element-1764" />

      <InfoBox type="tip" title="Testing on Real Devices" data-id="element-1765">
        Always test your mobile payment integration on real devices before going
        live. Emulators may not accurately simulate network conditions and
        security features like biometric authentication.
      </InfoBox>

      <InfoBox type="info" title="3D Secure on Mobile" data-id="element-1766">
        Both SDKs support 3D Secure authentication. The SDK will automatically
        present the 3DS challenge screen when required by the issuing bank.
      </InfoBox>
    </DocPageLayout>;
}